


import { apiGet, apiPost, apiCall } from './apiUtils';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sandbox.rebax.in';
/**
 * Analytics Service for React Website
 * Handles analytics and dashboard statistics API calls
 */

const analyticsService = {
  /**
   * Get dashboard statistics for Builder dashboard
   * @param {Object} filters - Optional filters (timeRange, startDate, endDate, cityManager, manager, subUser)
   * @returns {Promise<Object>} Dashboard statistics
   */
  getDashboardStats: async (filters = {}) => {
    try {
      const params = {
        time_range: filters.timeRange || 'this_month'
      };

      if (filters.startDate) {
        console.log('📅 Analytics Service - Received startDate:', filters.startDate);
        params.start_date = filters.startDate;
      }
      if (filters.endDate) {
        console.log('📅 Analytics Service - Received endDate:', filters.endDate);
        params.end_date = filters.endDate;
      }
      if (filters.cityManager) params.city_manager = filters.cityManager;
      if (filters.manager) params.manager = filters.manager;
      if (filters.subUser) params.sub_user = filters.subUser;
      if (filters.showOnlyAgentLeads) params.show_only_agent_leads = 'true';

      console.log('📡 Analytics Service - Final params before API call:', params);

      // If no auth token, return live demo data temporarily
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.log('No auth token, returning demo data with live structure');

        // Different data for different time ranges
        const demoData = {
          'this_month': {
            totalLeads: 1,
            activeLeads: 30,
            prospectLeads: 15,
            bookedLeads: 10,
            closedLeads: 5,
            newLeadsInPeriod: 50,
            siteVisitsDone: 20,
            tasksCompleted: 15,
            pendingTasks: 5,
            tasksInProgress: 10,
            invoicesSent: 8,
            invoicesPaid: 5,
            invoicesPending: 3,
            totalRevenue: 75000,
            dateRange: "30d",
            startDate: "2025-10-22",
            endDate: "2025-11-21"
          },
          'previous_month': {
            totalLeads: 0,
            activeLeads: 25,
            prospectLeads: 12,
            bookedLeads: 8,
            closedLeads: 4,
            newLeadsInPeriod: 40,
            siteVisitsDone: 15,
            tasksCompleted: 12,
            pendingTasks: 6,
            tasksInProgress: 8,
            invoicesSent: 6,
            invoicesPaid: 4,
            invoicesPending: 2,
            totalRevenue: 60000,
            dateRange: "30d",
            startDate: "2025-09-22",
            endDate: "2025-10-21"
          },
          'this_year': {
            totalLeads: 300,
            activeLeads: 180,
            prospectLeads: 90,
            bookedLeads: 60,
            closedLeads: 30,
            newLeadsInPeriod: 300,
            siteVisitsDone: 120,
            tasksCompleted: 90,
            pendingTasks: 30,
            tasksInProgress: 60,
            invoicesSent: 48,
            invoicesPaid: 30,
            invoicesPending: 18,
            totalRevenue: 450000,
            dateRange: "365d",
            startDate: "2025-01-01",
            endDate: "2025-12-31"
          }
        };

        return {
          success: true,
          data: demoData[params.time_range || "this_month"],
          timeRange: params.time_range || "this_month",
          generatedAt: new Date().toISOString()
        };
      }

      return await apiGet('/api/analytics/leads', params);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch dashboard statistics',
        error: error.message,
      };
    }
  },

  /**
   * Get lead analytics for charts
   * @param {Object} filters - Optional filters (dateRange, timeRange)
   * @returns {Promise<Object>} Lead analytics data for charts
   */
  getLeadAnalytics: async (filters = {}) => {
    try {
      const params = {
        time_range: filters.timeRange || 'this_month'
      };

      if (filters.startDate) {
        console.log('📊 Lead Analytics - Received startDate:', filters.startDate);
        params.start_date = filters.startDate;
      }
      if (filters.endDate) {
        console.log('📊 Lead Analytics - Received endDate:', filters.endDate);
        params.end_date = filters.endDate;
      }

      // Add user filters (EXACT mobile app logic)
      if (filters.filterUserId) {
        console.log('📊 Lead Analytics - Received filterUserId:', filters.filterUserId);
        params.filter_user_id = filters.filterUserId;
      }
      if (filters.assignedTo) {
        console.log('📊 Lead Analytics - Received assignedTo:', filters.assignedTo);
        params.assigned_to = filters.assignedTo;
      }
      if (filters.showOnlyAgentLeads) {
        console.log('📊 Lead Analytics - Received showOnlyAgentLeads:', filters.showOnlyAgentLeads);
        params.show_only_agent_leads = filters.showOnlyAgentLeads ? 'true' : 'false';
      }

      console.log('📊 Lead Analytics - Final params before API call:', params);

      const response = await apiGet('/api/analytics/leads', params);

      // Filter out zero values from the response
      if (response.success && response.data && response.data.months) {
        response.data.months = response.data.months.map(month => {
          const filteredMonth = {};
          Object.keys(month).forEach(key => {
            if (month[key] !== 0 && month[key] !== '0') {
              filteredMonth[key] = month[key];
            }
          });
          return Object.keys(filteredMonth).length > 0 ? filteredMonth : null;
        }).filter(month => month !== null);
      }

      return response;
    } catch (error) {
      console.error('Error fetching lead analytics:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch lead analytics',
        error: error.message,
      };
    }
  },


  /**
   * Get source analytics for charts
   * @param {Object} filters - Optional filters (dateRange, timeRange)
   * @returns {Promise<Object>} Source analytics data for charts
   */
  getSourceAnalytics: async (filters = {}) => {
    try {
      const params = {
        time_range: filters.timeRange || 'this_month'
      };

      if (filters.startDate) {
        console.log('📈 Source Analytics - Received startDate:', filters.startDate);
        params.start_date = filters.startDate;
      }
      if (filters.endDate) {
        console.log('📈 Source Analytics - Received endDate:', filters.endDate);
        params.end_date = filters.endDate;
      }

      // Add user filters (EXACT mobile app logic)
      if (filters.filterUserId) {
        console.log('📈 Source Analytics - Received filterUserId:', filters.filterUserId);
        params.filter_user_id = filters.filterUserId;
      }
      if (filters.assignedTo) {
        console.log('📈 Source Analytics - Received assignedTo:', filters.assignedTo);
        params.assigned_to = filters.assignedTo;
      }
      if (filters.showOnlyAgentLeads) {
        console.log('📈 Source Analytics - Received showOnlyAgentLeads:', filters.showOnlyAgentLeads);
        params.show_only_agent_leads = filters.showOnlyAgentLeads ? 'true' : 'false';
      }

      console.log('📈 Source Analytics - Final params before API call:', params);

      // Use actual API response
      const response = await apiGet('/api/analytics/sources', params);

      // If no auth token, return live demo data with actual time range
      if (!localStorage.getItem('authToken')) {
        const demoData = {
          'this_month': {
            months: [{
              month: 'Jan',
              acres: 40,
              magicBricks: 30,
              housing: 20,
              nobroker: 15,
              walk: 10
            }]
          },
          'previous_month': {
            months: [{
              month: 'Jan',
              acres: 35,
              magicBricks: 25,
              housing: 18,
              nobroker: 12,
              walk: 8
            }]
          },
          'this_year': {
            months: [
              { month: 'Jan', acres: 40, magicBricks: 30, housing: 20, nobroker: 15, walk: 10 },
              { month: 'Feb', acres: 45, magicBricks: 35, housing: 22, nobroker: 17, walk: 12 },
              { month: 'Mar', acres: 50, magicBricks: 40, housing: 25, nobroker: 20, walk: 15 },
              { month: 'Apr', acres: 55, magicBricks: 45, housing: 28, nobroker: 22, walk: 18 },
              { month: 'May', acres: 60, magicBricks: 50, housing: 30, nobroker: 25, walk: 20 },
              { month: 'Jun', acres: 65, magicBricks: 55, housing: 35, nobroker: 28, walk: 22 },
              { month: 'Jul', acres: 70, magicBricks: 60, housing: 38, nobroker: 30, walk: 25 },
              { month: 'Aug', acres: 75, magicBricks: 65, housing: 40, nobroker: 32, walk: 28 },
              { month: 'Sep', acres: 80, magicBricks: 70, housing: 45, nobroker: 35, walk: 30 },
              { month: 'Oct', acres: 85, magicBricks: 75, housing: 48, nobroker: 38, walk: 32 },
              { month: 'Nov', acres: 90, magicBricks: 80, housing: 50, nobroker: 40, walk: 35 },
              { month: 'Dec', acres: 95, magicBricks: 85, housing: 55, nobroker: 42, walk: 38 }
            ]
          }
        };

        response.data = demoData[params.time_range || "this_month"];
      }

      // Filter out zero values from the response
      if (response.success && response.data && response.data.months) {
        response.data.months = response.data.months.map(month => {
          const filteredMonth = {};
          Object.keys(month).forEach(key => {
            if (month[key] !== 0 && month[key] !== '0') {
              filteredMonth[key] = month[key];
            }
          });
          return Object.keys(filteredMonth).length > 0 ? filteredMonth : null;
        }).filter(month => month !== null);
      }

      return response;
    } catch (error) {
      console.error('Error fetching source analytics:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch source analytics',
        error: error.message,
      };
    }
  },

  /**
   * Get agent dashboard statistics
   * @param {Object} filters - Optional filters (time_range, startDate, endDate)
   * @returns {Promise<Object>} Dashboard statistics
   */
  getAgentDashboard: async (filters = {}) => {
    try {
      const params = {};

      // Add filters to params if provided
      if (filters.timeRange) params.time_range = filters.timeRange;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;

      console.log('Fetching agent dashboard with filters:', params);

      // Use actual API response
      const response = await apiGet('/api/analytics/dashboard', params);

      // If no auth token, return live demo data with actual time range
      if (!localStorage.getItem('authToken')) {
        const demoData = {
          'this_month': {
            totalLeads: 1,
            activeLeads: 30,
            prospectLeads: 15,
            bookedLeads: 10,
            closedLeads: 5,
            siteVisitsDone: 20,
            tasksCompleted: 15,
            invoicesSent: 8
          },
          'previous_month': {
            totalLeads: 0,
            activeLeads: 25,
            prospectLeads: 12,
            bookedLeads: 8,
            closedLeads: 4,
            siteVisitsDone: 15,
            tasksCompleted: 12,
            invoicesSent: 6
          },
          'this_year': {
            totalLeads: 300,
            activeLeads: 180,
            prospectLeads: 90,
            bookedLeads: 60,
            closedLeads: 30,
            siteVisitsDone: 120,
            tasksCompleted: 90,
            invoicesSent: 48
          }
        };

        response.data = demoData[params.time_range || "this_month"];
      }

      // Filter out zero values from the response
      if (response.success && response.data && response.data.months) {
        response.data.months = response.data.months.map(month => {
          const filteredMonth = {};
          Object.keys(month).forEach(key => {
            if (month[key] !== 0 && month[key] !== '0') {
              filteredMonth[key] = month[key];
            }
          });
          return Object.keys(filteredMonth).length > 0 ? filteredMonth : null;
        }).filter(month => month !== null);
      }

      return response;
    } catch (error) {
      console.error('Error fetching agent dashboard:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch dashboard statistics',
      };
    }
  },

  /**
   * Get admin dashboard statistics
   * @param {Object} filters - Optional filters (dateRange, userType)
   * @returns {Promise<Object>} Admin dashboard data
   */
  getAdminDashboard: async (filters = {}) => {
    try {
      const params = {};

      // Send time_range exactly as frontend uses it
      if (filters.timeRange) params.time_range = filters.timeRange;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;

      console.log('📊 Fetching admin dashboard with filters:', params);

      return await apiGet('/api/analytics/admin/dashboard', params);
    } catch (error) {
      console.error('Error fetching admin dashboard:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch admin dashboard',
        error: error.message,
      };
    }
  },

  /**
   * Get user analytics chart data for admin (matches React Native app)
   * @param {string} timeRange - Time range filter
   * @param {Object} filters - Additional filters (startDate, endDate)
   * @returns {Promise<Object>} Chart data
   */
  getUserAnalyticsChart: async (timeRange, filters = {}) => {
    try {
      const params = {};
      // Exact app logic: send either dates OR time_range, never both
      if (filters.startDate && filters.endDate) {
        params.start_date = filters.startDate;
        params.end_date = filters.endDate;
      } else if (timeRange) {
        params.time_range = timeRange;
      }

      return await apiGet('/api/analytics/admin/user-analytics', params);
    } catch (error) {
      console.error('Error fetching user analytics chart:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch chart data',
        error: error.message,
      };
    }
  },

  /**
   * Get popular projects for admin (matches React Native app)
   * @param {Object|URLSearchParams} filters - Optional filters (city, state, limit, page)
   * @returns {Promise<Object>} Popular projects data
   */
  getPopularProjects: async (filters = {}) => {
    try {
      let params = {};

      // Handle URLSearchParams object
      if (filters instanceof URLSearchParams) {
        // Convert URLSearchParams to regular object
        for (const [key, value] of filters) {
          params[key] = value;
        }
        console.log('📊 Converted URLSearchParams to object:', params);
      } else {
        // Handle regular object filters
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (filters.page) params.page = filters.page;
        if (filters.limit || filters.per_page) params.limit = filters.limit || filters.per_page;
      }

      console.log('📊 Final params for popular projects API:', params);
      return await apiGet('/api/analytics/admin/popular-projects', params);
    } catch (error) {
      console.error('Error fetching popular projects:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get popular builders for admin (matches React Native app)
   * @param {Object} filters - Optional filters (city, state, limit)
   * @returns {Promise<Object>} Popular builders data
   */
  getPopularBuilders: async (filters = {}) => {
    try {
      let params = {};

      // Handle URLSearchParams object
      if (filters instanceof URLSearchParams) {
        // Convert URLSearchParams to regular object
        for (const [key, value] of filters) {
          params[key] = value;
        }
        console.log('📊 Converted URLSearchParams to object:', params);
      } else {
        // Handle regular object filters
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (filters.page) params.page = filters.page;
        if (filters.limit || filters.per_page) params.limit = filters.limit || filters.per_page;
      }

      console.log('📊 Final params for popular builders API:', params);
      return await apiGet('/api/analytics/admin/popular-builders', params);
    } catch (error) {
      console.error('Error fetching popular builders:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get popular agents for admin (matches React Native app)
   * @param {Object|URLSearchParams} filters - Optional filters (city, state, limit, page)
   * @returns {Promise<Object>} Popular agents data
   */
  getPopularAgents: async (filters = {}) => {
    try {
      let params = {};

      // Handle URLSearchParams object
      if (filters instanceof URLSearchParams) {
        // Convert URLSearchParams to regular object
        for (const [key, value] of filters) {
          params[key] = value;
        }
        console.log('📊 Converted URLSearchParams to object:', params);
      } else {
        // Handle regular object filters
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (filters.page) params.page = filters.page;
        if (filters.limit || filters.per_page) params.limit = filters.limit || filters.per_page;
      }

      console.log('📊 Final params for popular agents API:', params);
      return await apiGet('/api/analytics/admin/popular-agents', params);
    } catch (error) {
      console.error('Error fetching popular agents:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get popular agencies for admin (matches React Native app)
   * @param {Object|URLSearchParams} filters - Optional filters (city, state, limit, page)
   * @returns {Promise<Object>} Popular agencies data
   */
  getPopularAgencies: async (filters = {}) => {
    try {
      let params = {};

      // Handle URLSearchParams object
      if (filters instanceof URLSearchParams) {
        // Convert URLSearchParams to regular object
        for (const [key, value] of filters) {
          params[key] = value;
        }
        console.log('📊 Converted URLSearchParams to object:', params);
      } else {
        // Handle regular object filters
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (filters.page) params.page = filters.page;
        if (filters.limit || filters.per_page) params.limit = filters.limit || filters.per_page;
      }

      console.log('📊 Final params for popular agencies API:', params);
      return await apiGet('/api/analytics/admin/popular-agencies', params);
    } catch (error) {
      console.error('Error fetching popular agencies:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get list of states and cities
   * @returns {Promise<Object>} States and cities data
   * Response format: { success: true, data: { states: [...], cities: { stateName: [...cities] } } }
   */
  getCitiesAndStates: async () => {
    try {
      console.log('Fetching cities and states');

      return await apiGet('/api/analytics/admin/cities-states');
    } catch (error) {
      console.error('Error fetching cities and states:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch cities and states',
      };
    }
  },

  /**
   * Download admin report (matches React Native app)
   * @param {Object} filters - Optional filters (startDate, endDate, timeRange)
   * @returns {Promise<Object>} Report data
   */
  downloadAdminReport: async (filters = {}) => {
    try {
      const params = {};
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      if (filters.timeRange) params.time_range = filters.timeRange;

      return await apiGet('/api/analytics/admin/download-report', params);
    } catch (error) {
      console.error('Error downloading admin report:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get admin dashboard analytics (matches React Native app exactly)
   * @param {string} timeRange - Time range filter
   * @returns {Promise<Object>} Dashboard analytics data
   */
  getDashboardAnalytics: async (timeRange, startDate, endDate) => {
    try {
      const params = {};
      // Exact app logic: send either dates OR time_range, never both
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else if (timeRange) {
        params.time_range = timeRange;
      }

      return await apiGet('/api/analytics/admin/dashboard', params);
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch dashboard analytics',
        error: error.message,
      };
    }
  },

  /**
   * Get popular locations for admin (matches React Native app)
   * @param {Object|URLSearchParams} filters - Optional filters (city, state, limit, page)
   * @returns {Promise<Object>} Popular locations data
   */
  getPopularLocations: async (filters = {}) => {
    try {
      let params = {};

      // Handle URLSearchParams object
      if (filters instanceof URLSearchParams) {
        // Convert URLSearchParams to regular object
        for (const [key, value] of filters) {
          params[key] = value;
        }
        console.log('📊 Converted URLSearchParams to object:', params);
      } else {
        // Handle regular object filters
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (filters.page) params.page = filters.page;
        if (filters.limit || filters.per_page) params.limit = filters.limit || filters.per_page;
      }

      console.log('📊 Final params for popular locations API:', params);
      return await apiGet('/api/analytics/admin/popular-locations', params);
    } catch (error) {
      console.error('Error fetching popular locations:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get popular property types for admin (matches React Native app)
   * @param {Object|URLSearchParams} filters - Optional filters (city, state, limit, page)
   * @returns {Promise<Object>} Popular property types data
   */
  getPopularPropertyTypes: async (filters = {}) => {
    try {
      let params = {};

      // Handle URLSearchParams object
      if (filters instanceof URLSearchParams) {
        // Convert URLSearchParams to regular object
        for (const [key, value] of filters) {
          params[key] = value;
        }
        console.log('📊 Converted URLSearchParams to object:', params);
      } else {
        // Handle regular object filters
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (filters.page) params.page = filters.page;
        if (filters.limit || filters.per_page) params.limit = filters.limit || filters.per_page;
      }

      console.log('📊 Final params for popular property types API:', params);
      return await apiGet('/api/analytics/admin/popular-property-types', params);
    } catch (error) {
      console.error('Error fetching popular property types:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get popular community types data (matches React Native app exactly)
   * @param {Object} filters - Filter parameters (city, state, page, limit)
   * @returns {Promise<Object>} Popular community types data
   */
  getPopularCommunityTypes: async (filters = {}) => {
    try {
      let params = {};

      // Handle URLSearchParams object
      if (filters instanceof URLSearchParams) {
        // Convert URLSearchParams to regular object
        for (const [key, value] of filters) {
          params[key] = value;
        }
        console.log('📊 Converted URLSearchParams to object:', params);
      } else {
        // Handle regular object filters
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (filters.page) params.page = filters.page;
        if (filters.limit || filters.per_page) params.limit = filters.limit || filters.per_page;
      }

      console.log('📊 Final params for popular community types API:', params);
      return await apiGet('/api/analytics/admin/popular-community-types', params);
    } catch (error) {
      console.error('Error fetching popular community types:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get popular specs data (matches React Native app exactly)
   * @param {Object} filters - Filter parameters (city, state, page, limit)
   * @returns {Promise<Object>} Popular specs data
   */
  getPopularSpecs: async (filters = {}) => {
    try {
      let params = {};

      // Handle URLSearchParams object
      if (filters instanceof URLSearchParams) {
        // Convert URLSearchParams to regular object
        for (const [key, value] of filters) {
          params[key] = value;
        }
        console.log('📊 Converted URLSearchParams to object:', params);
      } else {
        // Handle regular object filters
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (filters.page) params.page = filters.page;
        if (filters.limit || filters.per_page) params.limit = filters.limit || filters.per_page;
      }

      console.log('📊 Final params for popular specs API:', params);
      return await apiGet('/api/analytics/admin/popular-specs', params);
    } catch (error) {
      console.error('Error fetching popular specs:', error);
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * Get popular list counts (matches React Native app exactly)
   * @returns {Promise<Object>} Popular list counts data
   */
  getPopularListCounts: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      return await apiGet('/api/admin/popular-list-counts', params);
    } catch (error) {
      console.error('Error fetching popular list counts:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch popular list counts',
        error: error.message,
      };
    }
  },
};

export default analyticsService;
